import React from 'react';
import moment from "moment";

import { Grid } from '@material-ui/core';

import NonFieldErrors from '../../../../../components/AssetModal/components/NonFieldErrors';
import {decimalStrToNumber} from "../../../../../utils/utils";
import SelectFormElementV2
  from "../../../../RiskProfiling/components/StepContent/components/formElement/SelectFormElementV2";
import InputFormElement
  , {CurrencyNumberFormat} from "../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement";
import CalendarFormElement
  from "../../../../RiskProfiling/components/StepContent/components/formElement/CalendarFormElement";
import {newDesignInputCustomClasses} from "../../../../RiskProfiling/constants";
import {newDesignInputCustomClassesViewOnly, PARTICIPATION_CATEGORY} from "../../../constants";
import {
  calculateAssetsPayment,
  choiceValue,
  dateToMoment,
  getCalculationDisabledReason,
  momentToDate
} from "../../utils";
import {END_DATE_SHOULD_BE_AFTER_START} from "../../../../Trades/constants";
import {
  NumberFormat,
  PercentageNumberFormat
} from "../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement/InputFormElement";
import AutoCalculateToggle from "../AutoCalculateToggle";
import useModelWithAutoCalculatedField from "../hooks/useModelWithAutoCalculatedField";


const DISTRIBUTION_TYPES = {
  ongoing: {
    id: 3,
    value: "3",
    label: "Laufend"
  },
  quarterly: {
    id: 4,
    value: "4",
    label: "Vierteljährlich"
  },
  halfYearly: {
    id: 5,
    value: "5",
    label: "Halbjährlich"
  },
  yearly: {
    id: 1,
    value: "1",
    label: 'Jährlich'
  },
  end_of_term: {
    id: 2,
    value: "2",
    label: 'Endfällig'
  }
};

const DISTRIBUTION_DAYS = {
  [DISTRIBUTION_TYPES.yearly.value]: 360,
  [DISTRIBUTION_TYPES.halfYearly.value]: 180,
  [DISTRIBUTION_TYPES.quarterly]: 90,
};

const DISTRIBUTION_TYPES_OPTIONS = Object.values(DISTRIBUTION_TYPES);


const equityAutoCalculationHelper = {
  fieldName: 'yearly_distribution',
  getInitialModel: (defaults) => ({
    name: defaults.name,
    category: choiceValue(defaults.category || defaults.sub_category),
    acquisition_date: dateToMoment(defaults.acquisition_date),
    value: decimalStrToNumber(defaults.value),
    current_valuation: decimalStrToNumber(defaults.current_valuation),
    past_dividends: decimalStrToNumber(defaults.past_dividends),
    valuation_date: dateToMoment(defaults.valuation_date),
    distribution_type: choiceValue(defaults.distribution_type),
    distribution_rate: defaults.distribution_rate,
    yearly_distribution: defaults.yearly_distribution,
    tvpi: defaults.tvpi,
    end_date: dateToMoment(defaults.end_date),
    updated: dateToMoment(defaults.updated),
  }),
  depsFields: ['acquisition_date', 'end_date', 'distribution_type', 'distribution_rate', 'value'],
  isAutoCalcAvailable: (model) => model.acquisition_date && model.end_date && model.distribution_type && model.distribution_rate && model.value,
  calculateFieldValue: (model) => {
    return calculateAssetsPayment(
      DISTRIBUTION_DAYS[model.distribution_type], model.acquisition_date, model.end_date, model.distribution_rate,
      model.value
    )
 },
 getCalculationDeps: (model) => [model.acquisition_date, model.end_date, model.distribution_type, model.distribution_rate, model.value]
};

const VALUE_LABEL = 'Zeichnungssumme';
const DISTRIBUTION_TYPE_LABEL = 'Ausschüttungsart';
const START_DATE_LABEL = 'Erwerbsdatum';
const END_DATE_LABEL = 'Ablaufdatum';
const DISTRIBUTION_RATE_LABEL = 'Ausschüttungssatz p.a.';

const AUTO_CALC_DISABLED_INFO = getCalculationDisabledReason([
  VALUE_LABEL, DISTRIBUTION_TYPE_LABEL, START_DATE_LABEL, END_DATE_LABEL, DISTRIBUTION_RATE_LABEL,
]);

const EquityForm = (props) => {

  const {
    handleFormChange,
    defaults,
    errors,
    viewOnly
  } = props;

  const [model, handleModelChange, currentYearlyDistribution, isAutoCalcAvailable] = useModelWithAutoCalculatedField(
    equityAutoCalculationHelper, defaults
  );

  // FIXME: replace with eventHandler (require rework of unsaved changes formData)
  React.useEffect(() => {
    handleFormChange && handleFormChange({
      ...model,
      yearly_distribution: currentYearlyDistribution,
      acquisition_date: momentToDate(model.acquisition_date),
      valuation_date: momentToDate(model.valuation_date),
      end_date: momentToDate(model.end_date),
    });
  }, [model, currentYearlyDistribution]);

  const inputClasses = viewOnly ? newDesignInputCustomClassesViewOnly : newDesignInputCustomClasses;

  return (
    <>
      {!viewOnly && (
        <Grid item xs={12}>
          <SelectFormElementV2
            label="Kategorie"
            dataId={'category-input'}
            value={model.category}
            options={PARTICIPATION_CATEGORY.subCategories}
            onChange={handleModelChange('category')}
            required
            newDesign
          />
        </Grid>
      )}
      <Grid item xs={12} sm={4}>
        <InputFormElement
          dataId={'name-input'}
          label={'Name / Bezeichnung'}
          required={!viewOnly}
          value={model.name}
          error={errors.name}
          onChange={handleModelChange('name')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputFormElement
          dataId={'value-input'}
          label={VALUE_LABEL}
          inputComponent={CurrencyNumberFormat}
          inputProps={{inputProps: {allowNegative: false}}}
          value={model.value}
          error={errors.value}
          onChange={handleModelChange('value')}
          required={!viewOnly}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <SelectFormElementV2
          label={DISTRIBUTION_TYPE_LABEL}
          value={model.distribution_type}
          options={DISTRIBUTION_TYPES_OPTIONS}
          onChange={handleModelChange('distribution_type')}
          dataId={'distribution_type-input'}
          custom_classes={inputClasses}
          disabled={viewOnly}
          addEmptyOption
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <CalendarFormElement
          dataId={'acquisition_date-input'}
          label={START_DATE_LABEL}
          value={model.acquisition_date}
          error={errors.acquisition_date}
          onChange={handleModelChange('acquisition_date')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <CalendarFormElement
          dataId={'end_date-input'}
          label={END_DATE_LABEL}
          minDate={model.acquisition_date || undefined}
          minError={END_DATE_SHOULD_BE_AFTER_START}
          value={model.end_date}
          error={errors.end_date}
          onChange={handleModelChange('end_date')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputFormElement
          label={DISTRIBUTION_RATE_LABEL}
          inputComponent={PercentageNumberFormat}
          inputProps={{inputProps: {allowNegative: false, isAllowed: (values) => {
            const {floatValue} = values;
            return !floatValue || floatValue <= 100;
          }}}}
          value={model.distribution_rate}
          error={errors.distribution_rate}
          onChange={handleModelChange('distribution_rate')}
          dataId={'distribution_rate-input'}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Grid container spacing={1}>
          {/*FIXME: DISABLED FOR NOW as part of BCA-10671*/}
          {/*{!viewOnly && (*/}
          {/*  <Grid item xs={'auto'}>*/}
          {/*    <AutoCalculateToggle*/}
          {/*      value={model.is_auto_calculation}*/}
          {/*      handleValueChanged={handleModelChange('is_auto_calculation')}*/}
          {/*      disabled={!isAutoCalcAvailable}*/}
          {/*      disableReason={!isAutoCalcAvailable && AUTO_CALC_DISABLED_INFO}*/}
          {/*    />*/}
          {/*  </Grid>*/}
          {/*)}*/}
          <Grid item xs>
            <InputFormElement
              dataId={'yearly_distribution-input'}
              label="Jährliche Ausschüttungen"
              inputComponent={CurrencyNumberFormat}
              inputProps={{inputProps: {allowNegative: false}}}
              value={currentYearlyDistribution}
              error={errors.yearly_distribution}
              onChange={handleModelChange('yearly_distribution')}
              disabled={viewOnly || model.is_auto_calculation}
              custom_classes={inputClasses}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputFormElement
          dataId={'past-dividends-input'}
          label="Bisherige Ausschüttungen"
          inputComponent={CurrencyNumberFormat}
          inputProps={{inputProps: {allowNegative: false}}}
          value={model.past_dividends}
          error={errors.past_dividends}
          onChange={handleModelChange('past_dividends')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputFormElement
          dataId={'tvpi-input'}
          label="TVPI"
          inputComponent={NumberFormat}
          inputProps={{inputProps: {allowNegative: false}}}
          value={model.tvpi}
          error={errors.tvpi}
          onChange={handleModelChange('tvpi')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputFormElement
          dataId={'current-valuation-input'}
          label="Bewertung"
          inputComponent={CurrencyNumberFormat}
          inputProps={{inputProps: {allowNegative: false}}}
          value={model.current_valuation}
          error={errors.current_valuation}
          onChange={handleModelChange('current_valuation')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <CalendarFormElement
          label="Bewertungsdatum"
          disableFuture
          dataId={'value_date-input'}
          value={model.valuation_date}
          error={errors.valuation_date}
          onChange={handleModelChange('valuation_date')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <CalendarFormElement
          dataId={'updated-input'}
          label="Stand vom"
          value={model.updated || moment()}
          disabled
          required={!viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      {!!errors['non_field_errors'] && <NonFieldErrors errors={errors['non_field_errors']}/>}
    </>
  )
};

EquityForm.defaultProps = {
  defaults: {},
  errors: {}
};

export default EquityForm;